import React, { useState } from "react";
import { Link } from "@/features/shell/components/shared/Link/Link";
import type { useNavTree } from "../hooks/useNavTree";
import { logoId } from "../navItems/static";
import { useShellStore } from "../stores/shellStore";
import { autoExpandedId } from "../utils/autoExpandedId";
import { ariaCurrent, isNavItemActive } from "../utils/isNavItemActive";
import type { NavItem, NavLocation } from "../types";
import { Navbar, type NavbarItemVariant } from "./Navbar";

// Google only honours data-nosnippet on span, div and section.
export const NavLabel = ({ item }: { item: NavItem }) => (
  <Navbar.Label>
    {item.noSnippet ? <span data-nosnippet>{item.text}</span> : item.text}
    {item.isNew && <Navbar.NewBadge />}
  </Navbar.Label>
);

const NavEntryContent = ({ item }: { item: NavItem }) => (
  <>
    <Navbar.Icon>{item.icon}</Navbar.Icon>
    <NavLabel item={item} />
    {item.badge !== undefined && <Navbar.Badge>{item.badge}</Navbar.Badge>}
  </>
);

type NavEntryProps = {
  item: NavItem;
  location: NavLocation;
  variant?: NavbarItemVariant;
};

const NavEntry = ({ item, location, variant }: NavEntryProps) => {
  const setMenuOpen = useShellStore((state) => state.setMenuOpen);
  const { target } = item;
  const content = <NavEntryContent item={item} />;
  const rowProps = Navbar.itemProps({
    variant,
    active: isNavItemActive(item, location),
  });

  return target?.kind === "link" ? (
    <Link
      noStyles
      to={target.href}
      {...rowProps}
      aria-current={ariaCurrent(item, location)}
    >
      {content}
    </Link>
  ) : (
    <button
      type="button"
      {...rowProps}
      aria-current={ariaCurrent(item, location)}
      onClick={() => {
        setMenuOpen(false);
        target?.run();
      }}
    >
      {content}
    </button>
  );
};

type NavGroupProps = {
  item: NavItem;
  location: NavLocation;
  expanded: boolean;
  onToggle: () => void;
};

const NavGroup = ({ item, location, expanded, onToggle }: NavGroupProps) => {
  const isFlyoutOpen = Navbar.useFlyoutGroupId() === item.id;

  if (!item.children) {
    return (
      <Navbar.Group>
        <NavEntry
          item={item}
          location={location}
          variant={item.id === logoId ? "logo" : "default"}
        />
      </Navbar.Group>
    );
  }

  const rows = item.children.map((child) => (
    <NavEntry key={child.id} item={child} location={location} variant="sub" />
  ));

  return (
    <Navbar.Group>
      <button
        type="button"
        {...Navbar.itemProps({
          active: isNavItemActive(item, location),
          flyout: item.id,
          isFlyoutOpen,
        })}
        aria-current={ariaCurrent(item, location)}
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <NavEntryContent item={item} />
        <Navbar.Caret />
      </button>
      <Navbar.Sub open={expanded}>{rows}</Navbar.Sub>
    </Navbar.Group>
  );
};

type NavGroupsProps = {
  items: NavItem[];
  location: NavLocation;
};

// The expanded vertical is picked once, on mount.
const NavGroups = ({ items, location }: NavGroupsProps) => {
  const [expandedId, setExpandedId] = useState(() =>
    autoExpandedId(items, location),
  );

  return items.map((item) => (
    <NavGroup
      key={item.id}
      item={item}
      location={location}
      expanded={expandedId === item.id}
      onToggle={() =>
        setExpandedId((current) => (current === item.id ? undefined : item.id))
      }
    />
  ));
};

type NavTreeProps = {
  tree: ReturnType<typeof useNavTree>;
  location: NavLocation;
};

export const NavFlyout = ({ tree, location }: NavTreeProps) => {
  const id = Navbar.useFlyoutGroupId();
  const item = tree.items.find((candidate) => candidate.id === id);
  if (!item?.children) return null;

  return (
    <Navbar.Flyout title={<NavLabel item={item} />}>
      {item.children.map((child) => (
        <NavEntry
          key={child.id}
          item={child}
          location={location}
          variant="sub"
        />
      ))}
    </Navbar.Flyout>
  );
};

export const NavTree = ({ tree, location }: NavTreeProps) => {
  const { items, isSettled, skeletonRows } = tree;
  const [hasSettled, setHasSettled] = useState(isSettled);

  if (isSettled && !hasSettled) {
    setHasSettled(true);
  }

  return hasSettled ? (
    <NavGroups items={items} location={location} />
  ) : (
    Array.from({ length: skeletonRows }, (_, index) => (
      <Navbar.SkeletonRow key={index} />
    ))
  );
};
